import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	TextField,
	Typography,
	alpha,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { searchAssets } from "../../../configuration/pages/asset/components/TreeAsset";
import { useUserContext } from "../../../context/userContext";
import { HelmetDexteel } from "../../../helmet/HelmetDexteel";
import { upsertDefaultAreaId } from "../../../repositorie/MESF-frontend-Repositorie";
import { ErrorModal } from "../../modals/error-modal";

export const AreaSelector = () => {
	const [open, setOpen] = useState<boolean>(false);
	const [error, setError] = useState("");

	const {
		state: { userId, defaultAreaId, defaultAreaName },
		actions: { setDefaultAreaId, setDefaultAreaName },
	} = useUserContext();

	const { data: rows, isError, error: e } = searchAssets();

	const areasList = rows?.filter((a) => a.CanBeDefaultAsset);

	const [fullWidth] = React.useState(true);
	const [defaultAreaIdToChange, setDefaultAreaIdToChange] =
		useState(defaultAreaId);
	const [defaultAreaNameToChange, setDefaultAreaNameToChange] =
		useState(defaultAreaName);

	const onClose = (value: boolean) => setOpen(value);
	const changeAreaActive = () => {
		setOpen(true);
	};

	const handleCancel = () => {
		setDefaultAreaIdToChange(defaultAreaId);
		setDefaultAreaNameToChange(defaultAreaName);
		onClose(false);
	};

	const handleClose = () => {
		setDefaultAreaIdToChange(defaultAreaId);
		setDefaultAreaNameToChange(defaultAreaName);
		setOpen(false);
	};

	const handleChangeAreaId = (areaId: number, areName: string) => {
		setDefaultAreaIdToChange(areaId);
		setDefaultAreaNameToChange(areName);
	};

	const handleChangeDefaultAreaId = async () => {
		upsertDefaultAreaId(userId as number, defaultAreaIdToChange as number).then(
			(res) => {
				if (res.ok) {
					setDefaultAreaId(defaultAreaIdToChange);
					setDefaultAreaName(defaultAreaNameToChange);
					setOpen(false);
				} else {
					setError(res.message);
				}
			},
		);
	};

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	useEffect(() => {
		if (!areasList) {
			return;
		}
		// Only update when defaultAreaId is explicitly set (not null/undefined)
		// This prevents resetting to "Plant" while data is still loading
		if (defaultAreaId !== null && defaultAreaId !== undefined) {
			const areaSelected = areasList.find(
				(area) => area?.AssetId === defaultAreaId,
			);
			if (areaSelected) {
				setDefaultAreaName(areaSelected.AssetName);
			} else {
				// Asset not found in list, default to Plant
				setDefaultAreaName("Plant");
			}
		}
	}, [defaultAreaId, areasList]);

	// Sync local state when modal opens
	useEffect(() => {
		if (open) {
			setDefaultAreaIdToChange(defaultAreaId);
			setDefaultAreaNameToChange(defaultAreaName || "Plant");
		}
	}, [open]);

	return (
		<>
			<Box mx={2}>
				<IconButton color="inherit" onClick={changeAreaActive} size="small">
					<Box
						sx={{
							paddingLeft: 0,
							color: "#FFF",
							cursor: "pointer",
							maxWidth: "100px",
							fontSize: ".8rem",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							display: "flex",
							alignItems: "center",
						}}
					>
						<PersonPinCircleIcon
							sx={{
								marginLeft: "-5px",
							}}
						/>
						{defaultAreaName}
					</Box>
				</IconButton>
			</Box>
			{open && <HelmetDexteel title="Change Default Area" />}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={"sm"}
				onClose={handleClose}
				aria-labelledby="confirmation-dialog-title"
				open={open}
				sx={{
					"& .MuiInputBase-input": {
						color: "#000 !important",
					},
					"& .MuiPaper-root": {
						height: "80vh",
					},
				}}
			>
				<DialogTitle id="confirmation-dialog-title">AREA</DialogTitle>
				<DialogContent dividers sx={{ overflowY: "hidden" }}>
					<Typography
						fontSize={15}
						variant="subtitle1"
						style={{ marginBottom: 0 }}
					>
						Current Area
					</Typography>
					<Grid2 container>
						<Grid2 size={{ xs: 12 }} style={{ marginTop: 5 }}>
							<TextField
								variant="outlined"
								disabled
								value={defaultAreaNameToChange}
								fullWidth
								margin="dense"
								autoComplete="off"
								style={{ color: "#000 !important" }}
							/>
							<Typography
								fontSize={15}
								variant="subtitle1"
								style={{ marginBottom: 0, marginTop: 5 }}
							>
								Change Default Area
							</Typography>
							<Grid2 size={{ xs: 12 }} style={{ marginTop: 10 }}>
								<Grid2
									size={{ xs: 12 }}
									sx={{
										height: "45vh",
										border: "1px solid rgba(0, 0, 0, 0.26)",
										borderRadius: "2px",
										overflowY: "auto",
									}}
								>
									<List dense style={{ padding: 0 }}>
										{areasList?.map((area) => (
											<ListItemButton
												key={`Area-${area?.AssetId}`}
												onClick={() =>
													handleChangeAreaId(area?.AssetId, area?.AssetName)
												}
												selected={area?.AssetId === defaultAreaIdToChange}
												sx={{
													"&.Mui-selected": {
														backgroundColor: (theme) =>
															alpha(theme.palette.info.main, 0.2),
														"&:hover": {
															backgroundColor: (theme) =>
																alpha(theme.palette.info.main, 0.4),
														},
													},
													cursor: "pointer",
													width: "100%",
												}}
											>
												<ListItemText primary={area?.AssetName} />
											</ListItemButton>
										))}
									</List>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</DialogContent>
				<DialogActions>
					<Button autoFocus onClick={handleCancel} color="primary">
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleChangeDefaultAreaId}
						color="primary"
					>
						Change Area
					</Button>
				</DialogActions>
			</Dialog>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
